import { useEffect, useMemo, useState } from "react";
import {
  ScanSearch,
  CircleCheck,
  CircleX,
  HelpCircle,
  TrendingDown,
  Flag,
  Star,
  Banknote,
  X,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { moneyFormatter } from "@/config/currency";
import { getDateRangeDates } from "@/lib/compare-date-utils";
import { DateRangeFilter, type DateRangeOption } from "@/components/clicks/DateRangeFilter";
import {
  useIndexationSummary,
  useIndexationCheckerHealth,
  useIndexationTargets,
  useVipTargetIds,
} from "@/hooks/useIndexationData";
import { INDEX_PAGE_SIZE } from "@/services/IndexationService";
import type {
  IndexSortCol,
  IndexStatusFilter,
  IndexTierFilter,
  IndexDmcaFilter,
  IndexEventFilter,
} from "@/services/IndexationService";
import { applyHeaderClick, orderedRules, toggleLock, type SortRule } from "@/components/clicks/clicksGridEngine";
import { IndexationGrid } from "@/components/indexation/IndexationGrid";
import { IndexationFreshness } from "@/components/indexation/IndexationFreshness";
import { defaultColumns, type IndexGrain } from "@/components/indexation/indexationColumns";

// Unified Indexation page. Indexation is the backbone; DMCA is a columns
// extension (design doc "Единая страница"). The table is configurable like
// Clicks — show/hide, reorder, server-side sort. /dmca redirects here.
// Variant B cards (State + trend): index health, flow (dropped / new DMCA in the
// picked range), exposed money (muted), data-trust — health/scope in a thin line.

const money = moneyFormatter();
const fmtInt = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString());
const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

function StatCard({
  label,
  value,
  icon: Icon,
  tone,
  subtitle,
  active,
  onClick,
}: {
  label: string;
  value: string;
  icon: typeof ScanSearch;
  tone?: "positive" | "negative" | "muted";
  subtitle?: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <Card
      className={cn(
        onClick && "cursor-pointer transition-colors hover:border-primary/50",
        active && "border-primary ring-1 ring-primary",
      )}
      onClick={onClick}
    >
      <CardContent className="flex items-center gap-4 py-5">
        <div
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground",
            tone === "positive" && "bg-emerald-600/10 text-emerald-600",
            tone === "negative" && "bg-destructive/10 text-destructive",
          )}
        >
          <Icon className="size-5" />
        </div>
        <div className="min-w-0">
          <div className="text-2xl font-bold tabular-nums">{value}</div>
          <div className="truncate text-sm text-muted-foreground">{label}</div>
          {subtitle ? <div className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</div> : null}
        </div>
      </CardContent>
    </Card>
  );
}

export default function IndexationPage() {
  const [kind, setKind] = useState<IndexGrain>("domain");
  const [status, setStatus] = useState<IndexStatusFilter>("all");
  const [dmca, setDmca] = useState<IndexDmcaFilter>("all");
  const [vipOnly, setVipOnly] = useState(false);
  const [eventType, setEventType] = useState<IndexEventFilter | null>(null);
  const [tier, setTier] = useState<IndexTierFilter>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // Date range — same component + presets as Clicks. Default 30d ≈ the money window.
  const [dateRange, setDateRange] = useState<DateRangeOption>("last_30_days");
  const [customFrom, setCustomFrom] = useState<string>();
  const [customTo, setCustomTo] = useState<string>();
  const [sortRules, setSortRules] = useState<SortRule[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(INDEX_PAGE_SIZE);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => defaultColumns("domain"));

  // Debounce the search box so we don't fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Resolve the preset/custom range to instants (local day → UTC). Incomplete
  // custom range = no range (null), so a half-typed range doesn't silently clip.
  const range = useMemo(() => {
    if (dateRange === "custom" && (!customFrom || !customTo)) return { from: null, to: null };
    const { current } = getDateRangeDates(dateRange, undefined, customFrom, customTo);
    const from = current.start ? new Date(`${current.start}T00:00:00`).toISOString() : null;
    const to = new Date(`${current.end}T23:59:59.999`).toISOString();
    return { from, to };
  }, [dateRange, customFrom, customTo]);

  const sorts = useMemo(
    () =>
      orderedRules(sortRules).map((r) => ({
        col: r.id as IndexSortCol,
        dir: r.desc ? ("desc" as const) : ("asc" as const),
      })),
    [sortRules],
  );

  const { data: summary } = useIndexationSummary(range.from, range.to);
  const { data: health } = useIndexationCheckerHealth();
  const { data: vipIdsArr } = useVipTargetIds();
  const vipIds = useMemo(() => new Set(vipIdsArr ?? []), [vipIdsArr]);

  const {
    data: targets,
    isPending,
    isError,
    isPlaceholderData,
  } = useIndexationTargets({
    kind,
    status,
    dmca,
    vipOnly,
    vipIds: vipIdsArr ?? [],
    eventType: eventType ?? undefined,
    // Range feeds the table only when a flow card set an event type.
    from: eventType ? range.from : null,
    to: eventType ? range.to : null,
    tier,
    geo: null,
    search,
    sorts,
    page,
    pageSize,
  });

  const rows = targets?.rows ?? [];
  const total = targets?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Per-tier freshness (T1 vs 24h, T2 vs 7d), not blended — see get_index_summary.
  const t1Total = summary?.domains_tier1 ?? 0;
  const t2Total = summary?.domains_tier2 ?? 0;
  const t1Pct = t1Total > 0 ? Math.round(((summary?.domains_tier1_fresh ?? 0) / t1Total) * 100) : null;
  const t2Pct = t2Total > 0 ? Math.round(((summary?.domains_tier2_fresh ?? 0) / t2Total) * 100) : null;

  const isUrl = kind === "url";
  const s = summary;

  // Set a single-signal view (clears siblings) and apply this card's filter.
  const applyFilter = (patch: {
    status?: IndexStatusFilter;
    dmca?: IndexDmcaFilter;
    vipOnly?: boolean;
    eventType?: IndexEventFilter | null;
  }) => {
    setStatus(patch.status ?? "all");
    setDmca(patch.dmca ?? "all");
    setVipOnly(patch.vipOnly ?? false);
    setEventType(patch.eventType ?? null);
    setPage(1);
  };

  // Variant B cards for the active grain. Flow cards (dropped/new DMCA) read the
  // range-scoped summary counts and, on click, filter the table to that event.
  const cards = isUrl
    ? [
        { key: "indexed", label: "Indexed", value: `${pct(s?.urls_indexed ?? 0, s?.urls ?? 0)}%`,
          icon: CircleCheck, tone: "positive" as const, subtitle: `${fmtInt(s?.urls_indexed)} of ${fmtInt(s?.urls)}`,
          onClick: () => applyFilter({ status: "indexed" }), active: status === "indexed" && !eventType },
        { key: "dropped_clean", label: "Dropped · no DMCA", value: fmtInt(s?.urls_dropped_clean_in_range),
          icon: TrendingDown, tone: "negative" as const, subtitle: "de-indexed, no takedown · investigate",
          onClick: () => applyFilter({ status: "not_indexed", dmca: "none", eventType: "dropped" }),
          active: eventType === "dropped" && dmca === "none" },
        { key: "dropped_dmca", label: "Dropped · DMCA", value: fmtInt(s?.urls_dropped_dmca_in_range),
          icon: TrendingDown, tone: "negative" as const, subtitle: "de-indexed via takedown",
          onClick: () => applyFilter({ status: "not_indexed", dmca: "flagged", eventType: "dropped" }),
          active: eventType === "dropped" && dmca === "flagged" },
        { key: "dmca", label: "New DMCA in range", value: fmtInt(s?.urls_flagged_in_range),
          icon: Flag, tone: "negative" as const, subtitle: "notices that arrived in the period",
          onClick: () => applyFilter({ dmca: "flagged", eventType: "flagged" }), active: eventType === "flagged" },
        { key: "notidx", label: "Not indexed", value: fmtInt(s?.urls_not_indexed),
          icon: CircleX, tone: "muted" as const, subtitle: "currently out of the index",
          onClick: () => applyFilter({ status: "not_indexed" }), active: status === "not_indexed" && !eventType },
        { key: "unchecked", label: "Not checked", value: fmtInt(s?.urls_unchecked),
          icon: HelpCircle, subtitle: "no verdict yet",
          onClick: () => applyFilter({ status: "unchecked" }), active: status === "unchecked" && !eventType },
      ]
    : [
        { key: "indexed", label: "Indexed", value: `${pct(s?.domains_indexed ?? 0, s?.domains ?? 0)}%`,
          icon: CircleCheck, tone: "positive" as const, subtitle: `${fmtInt(s?.domains_indexed)} of ${fmtInt(s?.domains)}`,
          onClick: () => applyFilter({ status: "indexed" }), active: status === "indexed" && !eventType },
        { key: "dropped_clean", label: "Dropped · no DMCA", value: fmtInt(s?.domains_dropped_clean_in_range),
          icon: TrendingDown, tone: "negative" as const, subtitle: "de-indexed, no takedown · investigate",
          onClick: () => applyFilter({ status: "not_indexed", dmca: "none", eventType: "dropped" }),
          active: eventType === "dropped" && dmca === "none" },
        { key: "dropped_dmca", label: "Dropped · DMCA", value: fmtInt(s?.domains_dropped_dmca_in_range),
          icon: TrendingDown, tone: "negative" as const, subtitle: "de-indexed via takedown",
          onClick: () => applyFilter({ status: "not_indexed", dmca: "flagged", eventType: "dropped" }),
          active: eventType === "dropped" && dmca === "flagged" },
        { key: "dmca", label: "New DMCA in range", value: fmtInt(s?.domains_flagged_in_range),
          icon: Flag, tone: "negative" as const, subtitle: `${fmtInt(s?.domains_flagged)} flagged total`,
          onClick: () => applyFilter({ dmca: "flagged", eventType: "flagged" }), active: eventType === "flagged" },
        { key: "money", label: "Revenue exposed", value: money.format(s?.revenue_bleeding ?? 0),
          icon: Banknote, tone: "muted" as const,
          subtitle: `on ${fmtInt(s?.domains_bleeding)} de-indexed · +${money.format(s?.revenue_at_risk ?? 0)} flagged`,
          onClick: () => applyFilter({ status: "not_indexed" }), active: status === "not_indexed" && !eventType },
        { key: "unchecked", label: "Not checked", value: fmtInt(s?.domains_unchecked),
          icon: HelpCircle, subtitle: t1Pct != null ? `fresh — T1 ${t1Pct}% · T2 ${t2Pct ?? "—"}%` : "no verdict yet",
          onClick: () => applyFilter({ status: "unchecked" }), active: status === "unchecked" && !eventType },
      ];

  // Grain change resets the grain-specific view: default columns, page, sort, filters.
  const onKind = (v: IndexGrain) => {
    setKind(v);
    setPage(1);
    setVisibleColumns(defaultColumns(v));
    setSortRules([]);
    setEventType(null);
    setStatus("all");
    setDmca("all");
  };
  const onStatus = (v: IndexStatusFilter) => { setStatus(v); setEventType(null); setPage(1); };
  const onDmca = (v: IndexDmcaFilter) => { setDmca(v); setEventType(null); setPage(1); };
  const onTier = (v: IndexTierFilter) => { setTier(v); setPage(1); };
  const onVipToggle = () => { setVipOnly((v) => !v); setPage(1); };
  const onDateRange = (v: DateRangeOption) => { setDateRange(v); setPage(1); };
  const onCustomDate = (type: "from" | "to", date: string) => {
    if (type === "from") setCustomFrom(date);
    else setCustomTo(date);
    setPage(1);
  };
  const clearEvent = () => { setEventType(null); setPage(1); };
  const onPageSize = (v: number) => { setPageSize(v); setPage(1); };
  const onHeaderSort = (id: string, additive: boolean) => {
    setSortRules((rules) => applyHeaderClick(rules, id, additive));
    setPage(1);
  };
  const onLockSort = (id: string) => {
    setSortRules((rules) => toggleLock(rules, id));
    setPage(1);
  };

  const eventLabel = eventType === "dropped" ? "Dropped in range" : eventType === "flagged" ? "New DMCA in range" : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Indexation</h1>
        <p className="text-muted-foreground">
          Whether our domains and URLs are in Google's index, with the DMCA signal
          alongside — both read from the same checks.
        </p>
      </div>

      {/* Grain tabs — the cards and table below describe the active tab. */}
      <Tabs value={kind} onValueChange={(v) => onKind(v as IndexGrain)}>
        <TabsList>
          <TabsTrigger value="domain">
            Domains{summary ? ` · ${fmtInt(summary.domains)}` : ""}
          </TabsTrigger>
          <TabsTrigger value="url">
            URLs{summary ? ` · ${fmtInt(summary.urls)}` : ""}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Variant B cards — index health · drops split by cause · new DMCA · money · data-trust. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {cards.map((c) => (
          <StatCard
            key={c.key}
            label={c.label}
            value={c.value}
            icon={c.icon}
            tone={c.tone}
            subtitle={c.subtitle}
            active={c.active}
            onClick={c.onClick}
          />
        ))}
      </div>

      {/* Checker health + coverage as badges — same language as Clicks/GSC sync badges. */}
      <IndexationFreshness health={health} summary={summary} grain={kind} />

      {/* Filters — date range (events/history scope), grain filters, VIP, search. */}
      <div className="flex flex-wrap items-center gap-3">
        <DateRangeFilter
          dateRange={dateRange}
          onDateRangeChange={onDateRange}
          compareEnabled={false}
          onCompareChange={() => {}}
          customFrom={customFrom}
          customTo={customTo}
          onCustomDateChange={onCustomDate}
          showCompare={false}
        />
        <Select value={status} onValueChange={(v) => onStatus(v as IndexStatusFilter)}>
          <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All index statuses</SelectItem>
            <SelectItem value="indexed">Indexed</SelectItem>
            <SelectItem value="not_indexed">Not indexed</SelectItem>
            <SelectItem value="unchecked">Not checked</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dmca} onValueChange={(v) => onDmca(v as IndexDmcaFilter)}>
          <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All DMCA</SelectItem>
            <SelectItem value="flagged">DMCA flagged</SelectItem>
            <SelectItem value="none">No DMCA (clean)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(tier)} onValueChange={(v) => onTier(v === "all" ? "all" : (Number(v) as 1 | 2 | 3))}>
          <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            <SelectItem value="1">Tier 1</SelectItem>
            <SelectItem value="2">Tier 2</SelectItem>
            <SelectItem value="3">Tier 3</SelectItem>
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={onVipToggle}
          className={cn(
            "inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors",
            vipOnly
              ? "border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-400"
              : "border-border bg-card text-muted-foreground hover:bg-muted",
          )}
          title="Show only VIP (pinned) targets"
        >
          <Star className={cn("size-3.5", vipOnly && "fill-current")} /> VIP only
        </button>
        <Input
          placeholder="Search domain / URL…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-[240px]"
        />
        {eventLabel ? (
          <button
            type="button"
            onClick={clearEvent}
            className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-primary bg-primary/10 px-3 text-sm font-medium text-primary"
            title="Clear the in-range event filter"
          >
            {eventLabel} <X className="size-3.5" />
          </button>
        ) : null}
      </div>

      {kind === "url" ? (
        <p className="-mb-2 text-xs text-muted-foreground">
          URL-level index/DMCA status exists only for domains flagged for a takedown — most URLs read “Not checked” by design.
        </p>
      ) : null}

      {/* The grain remounts the grid (key), so expansions reset on switch. The
          range is passed through for the per-row history drawer. */}
      <IndexationGrid
        key={kind}
        grain={kind}
        rows={rows}
        total={total}
        isPending={isPending}
        isError={isError}
        isPlaceholder={isPlaceholderData}
        visibleColumns={visibleColumns}
        onVisibleColumnsChange={setVisibleColumns}
        vipIds={vipIds}
        historyFrom={range.from}
        historyTo={range.to}
        sortRules={sortRules}
        onHeaderSort={onHeaderSort}
        onLockSort={onLockSort}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={onPageSize}
      />

      {/* Honesty caveats — the site: signal is directional, money is last night's. */}
      <div className="space-y-1 text-xs text-muted-foreground">
        <p>
          The date range scopes the flow cards (“Dropped / New DMCA in range”) and each row's history —
          not the current-state cards (Indexed, Not indexed, Revenue exposed) or the table, which stay
          current unless you click a flow card. “Dropped in range” counts the current episode's onset in
          the window and still-down now; recovered-in-range and flapping come later.
        </p>
        <p>
          “Indexed” means Google showed at least one of our URLs for that domain — a loose signal, not
          ranking or traffic. A single site: miss can be transient, so confirm in GSC before acting.
        </p>
        <p>
          Money columns (Clicks, Revenue, Conversions, Leads, Sales, Profit) are materialized nightly and
          exact per row — see the “money as of” note above the table. Red bar: dropped out of the index on
          a money or DMCA target. Amber bar: a DMCA notice but still indexed.
        </p>
      </div>
    </div>
  );
}
